import { cloneDeep } from 'lodash';
import {
    getProductMainCategories,
    normalizeSystemValue,
} from '@/data/productCategories';
import {
    builderFormToCalculatorInput,
    calculateProductPricing,
    createDefaultFurnizorPricingForm,
    mergeFormWithFurnizorPricing,
    parseNumericInput,
} from '@/utils/priceListProductCalculator';
import { pricingDetailToBuilderForm } from '@/utils/pricingDetailMapper';
import {
    isDraftFurnizorTabNewOrModified,
    resolveDraftDisplayKind,
    resolveLiveFurnizorTabsForDraftCompare,
} from '@/utils/priceListTablePricing';

/** Editable purchase fields cloned into the simulation sandbox... */
export const SUPPLIER_COMPARE_EDIT_FIELDS = [
    'supplierListPriceNew',
    'discount1',
    'discount2',
    'cashDiscount',
    'marketingDiscount',
    'discounts',
    'transportCostEstimated',
    'transportCostRate',
    'transportCostBreakdown',
    'stampDutyAmount',
    'stampDutyCurrency',
    'stampDutyBreakdown',
    'squareMetersPerTruck',
    'currency',
];

/** Prefix for lista-în-lucru twin columns in the compare matrix. */
export const DRAFT_COLUMN_PREFIX = 'draft:';

/**
 * @param {string} baseKey
 * @returns {string}
 */
export function toDraftColumnKey(baseKey) {
    return `${DRAFT_COLUMN_PREFIX}${String(baseKey || '')}`;
}

/**
 * @param {string|null|undefined} columnKey
 * @returns {boolean}
 */
export function isDraftColumnKey(columnKey) {
    return String(columnKey || '').startsWith(DRAFT_COLUMN_PREFIX);
}

/**
 * @param {string|null|undefined} columnKey
 * @returns {string}
 */
export function resolveBaseSupplierColumnKey(columnKey) {
    const key = String(columnKey || '');
    return isDraftColumnKey(key) ? key.slice(DRAFT_COLUMN_PREFIX.length) : key;
}

/**
 * Maps builder form tabs onto simulation columns, sending draft-flagged
 * furnizori to `draft:` twin keys so lista actuală columns stay intact.
 * @param {Record<string, unknown>} form
 * @param {Record<string, { fromDraftList?: boolean }>|null|undefined} draftColumnFlags
 * @returns {Record<string, Record<string, unknown>>}
 */
export function mapBuilderFormToSimulationColumns(form, draftColumnFlags = null) {
    const byColumn = {};
    (form?.furnizorPricings || []).forEach((pricing) => {
        const baseKey = resolveSupplierColumnKey(pricing, form);
        if (!baseKey) {
            return;
        }
        const useDraftKey = Boolean(draftColumnFlags?.[baseKey]?.fromDraftList);
        const columnKey = useDraftKey ? toDraftColumnKey(baseKey) : baseKey;
        byColumn[columnKey] = buildSimulationColumnEntry(pricing, form);
    });
    return byColumn;
}

/**
 * Flattens draft: keys for the product builder (tabs use base column keys).
 * @param {Record<string, Record<string, unknown>>|null|undefined} productSimulation
 * @param {boolean} [preferDraft=true]
 * @returns {Record<string, Record<string, unknown>>}
 */
export function flattenSimulationStateForBuilder(productSimulation, preferDraft = true) {
    const out = {};
    Object.entries(productSimulation || {}).forEach(([key, value]) => {
        const baseKey = resolveBaseSupplierColumnKey(key);
        const fromDraft = isDraftColumnKey(key);
        if (preferDraft) {
            if (fromDraft || out[baseKey] === undefined) {
                out[baseKey] = value;
            }
            return;
        }
        if (!fromDraft) {
            out[baseKey] = value;
        }
    });
    return out;
}

/** Sentinel for products that have 2+ furnizori (same as PriceList table filter). */
export const FURNIZOR_FILTER_MULTIPLE = '__multiple_furnizori__';

/**
 * @param {Record<string, unknown>|null|undefined} item
 * @returns {{ label: string, isDefault: boolean }[]}
 */
export function getProductFurnizorLabels(item) {
    const detail = item?.pricing_detail || {};
    const manufacturerName = String(detail.manufacturerName || '').trim();
    const pricings = Array.isArray(detail.furnizorPricings)
        ? detail.furnizorPricings
        : [];
    const defaultPricingId = detail.defaultFurnizorPricingId;

    if (!pricings.length) {
        const fallback = manufacturerName || String(item?.furnizor || '').trim();
        return fallback
            ? [{ label: fallback, isDefault: true }]
            : [];
    }

    const hasExplicitDefault = pricings.some((pricing) => (
        Boolean(pricing?.isDefault)
        || (
            defaultPricingId != null
            && pricing?.id != null
            && String(pricing.id) === String(defaultPricingId)
        )
    ));

    return pricings
        .map((pricing, index) => {
            const code = String(pricing.furnizorCode || '').trim();
            const name = String(pricing.furnizorName || '').trim();
            let label = '';
            if (pricing.usesManufacturerAsFurnizor) {
                label = manufacturerName || name || code || 'Producător';
            } else {
                label = name || code;
            }
            if (!label) {
                return null;
            }
            const isDefault = hasExplicitDefault
                ? (
                    Boolean(pricing.isDefault)
                    || (
                        defaultPricingId != null
                        && pricing?.id != null
                        && String(pricing.id) === String(defaultPricingId)
                    )
                )
                : index === 0;
            return { label, isDefault };
        })
        .filter(Boolean)
        .sort((a, b) => Number(b.isDefault) - Number(a.isDefault));
}

/**
 * @param {Record<string, unknown>|null|undefined} item
 * @param {string|null|undefined} selected
 * @returns {boolean}
 */
export function productMatchesFurnizorFilter(item, selected) {
    if (!selected) {
        return true;
    }
    const labels = getProductFurnizorLabels(item);
    if (selected === FURNIZOR_FILTER_MULTIPLE) {
        return labels.length >= 2;
    }
    const wanted = String(selected).trim().toLowerCase();
    return labels.some(
        (row) => String(row?.label || '').trim().toLowerCase() === wanted,
    );
}

/**
 * @param {Record<string, unknown>|null|undefined} item
 * @param {string|null|undefined} selected
 * @returns {boolean}
 */
export function productMatchesSystemFilter(item, selected) {
    const wanted = normalizeSystemValue(selected);
    if (!wanted) {
        return true;
    }
    return getProductMainCategories(item).some(
        (categoryName) => normalizeSystemValue(categoryName) === wanted,
    );
}

/**
 * @param {Record<string, unknown>|null|undefined} formOrContext
 * @returns {{ manufacturerName: string, manufacturerCode: string }}
 */
export function resolveManufacturerContext(formOrContext = {}) {
    return {
        manufacturerName: String(
            formOrContext?.manufacturerName
                || formOrContext?.manufacturer_name
                || '',
        ).trim(),
        manufacturerCode: String(
            formOrContext?.manufacturerCode
                || formOrContext?.manufacturer_code
                || '',
        ).trim(),
    };
}

/**
 * Stable column key for a furnizor across products.
 * @param {Record<string, unknown>|null|undefined} pricing
 * @param {{ manufacturerName?: string, manufacturerCode?: string }|Record<string, unknown>} [context]
 * @returns {string}
 */
export function resolveSupplierColumnKey(pricing, context = {}) {
    if (!pricing || typeof pricing !== 'object') {
        return 'unknown';
    }
    const mfr = resolveManufacturerContext(context);
    if (pricing.usesManufacturerAsFurnizor) {
        if (mfr.manufacturerCode) {
            return `mfr:${mfr.manufacturerCode.toLowerCase()}`;
        }
        if (mfr.manufacturerName) {
            return `mfr-name:${mfr.manufacturerName.toLowerCase()}`;
        }
    }
    if (pricing.furnizorId != null && pricing.furnizorId !== '') {
        return `id:${pricing.furnizorId}`;
    }
    const code = String(pricing.furnizorCode || '').trim().toLowerCase();
    if (code) {
        return `code:${code}`;
    }
    const name = String(pricing.furnizorName || '').trim().toLowerCase();
    if (name) {
        return `name:${name}`;
    }
    const clientKey = String(pricing.clientKey || '').trim();
    if (clientKey) {
        return `client:${clientKey}`;
    }
    return `fallback:${pricing.id ?? 'x'}`;
}

/**
 * @param {Record<string, unknown>|null|undefined} pricing
 * @param {{ manufacturerName?: string, manufacturerCode?: string }|Record<string, unknown>} [context]
 * @returns {string}
 */
export function resolveSupplierColumnLabel(pricing, context = {}) {
    if (!pricing || typeof pricing !== 'object') {
        return '—';
    }
    const mfr = resolveManufacturerContext(context);

    if (pricing.usesManufacturerAsFurnizor) {
        if (mfr.manufacturerName && mfr.manufacturerCode) {
            return `${mfr.manufacturerName} (${mfr.manufacturerCode})`;
        }
        if (mfr.manufacturerName) {
            return mfr.manufacturerName;
        }
        if (mfr.manufacturerCode) {
            return mfr.manufacturerCode;
        }
        const fallbackName = String(pricing.furnizorName || '').trim();
        const fallbackCode = String(pricing.furnizorCode || '').trim();
        if (fallbackName) {
            return fallbackName;
        }
        if (fallbackCode) {
            return fallbackCode;
        }
        return 'Producător';
    }

    const name = String(pricing.furnizorName || '').trim();
    const code = String(pricing.furnizorCode || '').trim();
    if (name && code) {
        return `${name} (${code})`;
    }
    if (name) {
        return name;
    }
    if (code) {
        return code;
    }
    return 'Furnizor';
}

/**
 * @param {Record<string, unknown>} pricing
 * @returns {Record<string, unknown>}
 */
export function extractEditableSimulationFields(pricing) {
    const out = {};
    SUPPLIER_COMPARE_EDIT_FIELDS.forEach((field) => {
        if (field === 'discounts') {
            out.discounts = Array.isArray(pricing.discounts) ? [...pricing.discounts] : [];
            return;
        }
        if (field === 'transportCostBreakdown') {
            out.transportCostBreakdown = cloneDeep(pricing.transportCostBreakdown || null);
            return;
        }
        if (field === 'stampDutyBreakdown') {
            out.stampDutyBreakdown = cloneDeep(pricing.stampDutyBreakdown || null);
            return;
        }
        out[field] = pricing[field];
    });
    return out;
}

/**
 * Full simulation column payload (editable fields + furnizor identity).
 * @param {Record<string, unknown>} pricing
 * @param {Record<string, unknown>} [formContext]
 * @returns {Record<string, unknown>}
 */
export function buildSimulationColumnEntry(pricing, formContext = {}) {
    return {
        ...extractEditableSimulationFields(pricing),
        clientKey: pricing.clientKey,
        furnizorId: pricing.furnizorId,
        furnizorCode: pricing.furnizorCode,
        furnizorName: pricing.furnizorName,
        usesManufacturerAsFurnizor: Boolean(pricing.usesManufacturerAsFurnizor),
        isDefault: Boolean(pricing.isDefault),
        manufacturerName: formContext.manufacturerName
            || pricing.manufacturerName
            || '',
        manufacturerCode: formContext.manufacturerCode
            || pricing.manufacturerCode
            || '',
    };
}

/**
 * Hydrate a builder furnizor-pricing row from a simulation column entry.
 * @param {Record<string, unknown>|null|undefined} simEntry
 * @returns {Record<string, unknown>}
 */
export function simulationEntryToPricing(simEntry = {}) {
    const editable = extractEditableSimulationFields(simEntry);
    const overrides = {
        furnizorId: simEntry.furnizorId ?? null,
        furnizorCode: simEntry.furnizorCode || '',
        furnizorName: simEntry.furnizorName || '',
        usesManufacturerAsFurnizor: Boolean(simEntry.usesManufacturerAsFurnizor),
        isDefault: Boolean(simEntry.isDefault),
        ...editable,
    };
    if (simEntry.clientKey) {
        overrides.clientKey = simEntry.clientKey;
    }
    return createDefaultFurnizorPricingForm(overrides);
}

/**
 * @param {Record<string, unknown>|null|undefined} draft
 * @returns {boolean}
 */
export function hasUsableDraftBuilderPricing(draft) {
    if (!draft || draft.updateMode === 'simple') {
        return false;
    }
    const formTabs = draft?.builderState?.productBuilderForm?.furnizorPricings;
    if (Array.isArray(formTabs) && formTabs.length > 0) {
        return true;
    }
    const payloadTabs = draft?.payload?.pricing_detail?.furnizorPricings;
    return Array.isArray(payloadTabs) && payloadTabs.length > 0;
}

/**
 * Prefer the lista-în-lucru builder form; fall back to draft payload pricing_detail.
 * @param {Record<string, unknown>|null|undefined} draft
 * @param {Record<string, unknown>|null|undefined} liveItem
 * @returns {Record<string, unknown>|null}
 */
export function resolveDraftCompareBuilderForm(draft, liveItem = null) {
    if (!hasUsableDraftBuilderPricing(draft)) {
        return null;
    }
    const form = draft?.builderState?.productBuilderForm;
    if (Array.isArray(form?.furnizorPricings) && form.furnizorPricings.length > 0) {
        return cloneDeep(form);
    }
    const detail = draft?.payload?.pricing_detail;
    if (!detail || typeof detail !== 'object') {
        return null;
    }
    const mapped = pricingDetailToBuilderForm(
        detail,
        liveItem || draft?.payload || {},
    );
    return mapped.productBuilderForm || mapped || null;
}

/**
 * Marks only new / changed furnizor columns that come from lista în lucru.
 * Uses the same new/modified rules as the draft panel so unchanged existing
 * suppliers (e.g. default Hy Pipe when only new furnizori were added) stay
 * on a single lista-actuală column.
 * @param {Record<string, unknown>|null|undefined} draft
 * @param {Record<string, unknown>|null|undefined} liveItem
 * @param {Record<string, unknown>|null|undefined} form
 * @returns {Record<string, { fromDraftList: boolean, draftKind: string }>}
 */
export function resolveDraftCompareColumnFlags(draft, liveItem, form) {
    const flags = {};
    const pricings = Array.isArray(form?.furnizorPricings) ? form.furnizorPricings : [];
    if (!draft || !pricings.length) {
        return flags;
    }

    const action = String(draft?.action || draft?.summary?.action || '').trim();
    if (action === 'create') {
        pricings.forEach((pricing) => {
            const key = resolveSupplierColumnKey(pricing, form);
            if (!key) {
                return;
            }
            flags[key] = {
                fromDraftList: true,
                draftKind: resolveDraftDisplayKind(draft, liveItem, pricing),
            };
        });
        return flags;
    }

    const liveTabs = resolveLiveFurnizorTabsForDraftCompare(liveItem);
    const payloadTabs = Array.isArray(draft?.payload?.pricing_detail?.furnizorPricings)
        ? draft.payload.pricing_detail.furnizorPricings
        : [];
    const siblingTabs = payloadTabs.length ? payloadTabs : pricings;

    pricings.forEach((pricing) => {
        const key = resolveSupplierColumnKey(pricing, form);
        if (!key) {
            return;
        }
        if (!isDraftFurnizorTabNewOrModified(
            pricing,
            liveTabs,
            payloadTabs,
            draft,
            siblingTabs,
            liveItem,
        )) {
            return;
        }
        flags[key] = {
            fromDraftList: true,
            draftKind: resolveDraftDisplayKind(draft, liveItem, pricing),
        };
    });

    return flags;
}

/**
 * @param {Record<string, unknown>} item
 * @param {Record<string, unknown>|null|undefined} [draft]
 * @returns {{
 *   productCode: string,
 *   productName: string,
 *   form: Record<string, unknown>,
 *   pricings: Record<string, unknown>[],
 *   liveForm: Record<string, unknown>|null,
 *   livePricings: Record<string, unknown>[],
 *   draftColumnFlags: Record<string, { fromDraftList: boolean, draftKind: string }>,
 *   hasDraftPricing: boolean,
 * }}
 */
export function buildProductCompareSource(item, draft = null) {
    const productCode = String(item?.product_code || item?.part_no || '').trim();
    const mapped = pricingDetailToBuilderForm(item?.pricing_detail || {}, item || {});
    const liveForm = mapped.productBuilderForm || mapped;
    const livePricings = Array.isArray(liveForm?.furnizorPricings)
        ? liveForm.furnizorPricings
        : [];
    const draftForm = resolveDraftCompareBuilderForm(draft, item);
    const form = draftForm || liveForm;
    const pricings = Array.isArray(form.furnizorPricings) ? form.furnizorPricings : [];
    const hasDraftPricing = Boolean(draftForm);
    return {
        productCode,
        productName: String(
            item?.product_name
            || item?.name
            || form.productName
            || draft?.payload?.product_name
            || productCode,
        ),
        form,
        pricings,
        liveForm: hasDraftPricing ? liveForm : null,
        livePricings: hasDraftPricing ? livePricings : [],
        draftColumnFlags: hasDraftPricing
            ? resolveDraftCompareColumnFlags(draft, item, form)
            : {},
        hasDraftPricing,
    };
}

/**
 * Builds baseline simulation_state from official (+ draft twin) pricings.
 * @param {Array<{
 *   productCode: string,
 *   form: Record<string, unknown>,
 *   pricings: Record<string, unknown>[],
 *   liveForm?: Record<string, unknown>|null,
 *   livePricings?: Record<string, unknown>[],
 *   draftColumnFlags?: Record<string, { fromDraftList?: boolean }>,
 *   hasDraftPricing?: boolean,
 * }>} sources
 * @returns {Record<string, Record<string, Record<string, unknown>>>>}
 */
export function buildBaselineSimulationState(sources) {
    const state = {};
    sources.forEach((source) => {
        const byColumn = {};
        if (source.hasDraftPricing) {
            const liveForm = source.liveForm || source.form || {};
            (source.livePricings || []).forEach((pricing) => {
                const columnKey = resolveSupplierColumnKey(pricing, liveForm);
                if (!columnKey) {
                    return;
                }
                byColumn[columnKey] = buildSimulationColumnEntry(pricing, liveForm);
            });
            const draftForm = source.form || {};
            (source.pricings || []).forEach((pricing) => {
                const baseKey = resolveSupplierColumnKey(pricing, draftForm);
                if (!baseKey || !source.draftColumnFlags?.[baseKey]?.fromDraftList) {
                    return;
                }
                byColumn[toDraftColumnKey(baseKey)] = buildSimulationColumnEntry(
                    pricing,
                    draftForm,
                );
            });
        } else {
            const mfrContext = source.form || {};
            (source.pricings || []).forEach((pricing) => {
                const columnKey = resolveSupplierColumnKey(pricing, mfrContext);
                if (!columnKey) {
                    return;
                }
                byColumn[columnKey] = buildSimulationColumnEntry(pricing, mfrContext);
            });
        }
        state[source.productCode] = byColumn;
    });
    return state;
}

/**
 * Merge saved simulation overrides onto baseline (baseline wins for missing keys).
 * @param {Record<string, Record<string, Record<string, unknown>>>>} baseline
 * @param {Record<string, Record<string, Record<string, unknown>>>>|null|undefined} saved
 * @returns {Record<string, Record<string, Record<string, unknown>>>>}
 */
export function mergeSimulationState(baseline, saved) {
    const merged = cloneDeep(baseline || {});
    if (!saved || typeof saved !== 'object') {
        return merged;
    }
    Object.keys(saved).forEach((productCode) => {
        if (!merged[productCode]) {
            merged[productCode] = {};
        }
        const savedCols = saved[productCode] || {};
        Object.keys(savedCols).forEach((columnKey) => {
            merged[productCode][columnKey] = {
                ...(merged[productCode][columnKey] || {}),
                ...savedCols[columnKey],
            };
        });
    });
    return merged;
}

/**
 * @param {Record<string, unknown>} form
 * @param {Record<string, unknown>} pricing
 * @param {Record<string, unknown>|null|undefined} simulationFields
 * @param {Record<string, unknown>} pricingSettings
 * @returns {Record<string, unknown>}
 */
export function computeSimulatedEntryPricing(form, pricing, simulationFields, pricingSettings) {
    const mergedPricing = {
        ...pricing,
        ...(simulationFields || {}),
    };
    const calculatorInput = builderFormToCalculatorInput(
        mergeFormWithFurnizorPricing(form, mergedPricing),
    );
    return calculateProductPricing(calculatorInput, pricingSettings);
}

/**
 * @param {object} params
 * @param {Array<{ productCode: string, productName: string, form: Record<string, unknown>, pricings: Record<string, unknown>[] }>} params.sources
 * @param {string} params.referenceProductCode
 * @param {Record<string, Record<string, Record<string, unknown>>>>} params.simulationState
 * @param {Record<string, unknown>} params.pricingSettings
 * @returns {{
 *   columns: Array<{ key: string, label: string }>,
 *   rows: Array<{
 *     productCode: string,
 *     productName: string,
 *     cells: Record<string, {
 *       present: boolean,
 *       entryPriceRonExVat: number|null,
 *       finalEntryPriceExVat: number|null,
 *       currency: string,
 *       isLowest: boolean,
 *       deltaVsMin: number|null,
 *       deltaPctVsMin: number|null,
 *       tooltip: string,
 *       simulation: Record<string, unknown>|null,
 *       pricing: Record<string, unknown>|null,
 *     }>,
 *     minEntryPriceRonExVat: number|null,
 *   }>
 * }}
 */
export function buildSupplierCompareMatrix({
    sources,
    referenceProductCode,
    simulationState,
    pricingSettings,
}) {
    const columnMeta = new Map();
    const registerColumn = (key, label) => {
        if (!columnMeta.has(key)) {
            columnMeta.set(key, { key, label });
            return;
        }
        const existing = columnMeta.get(key);
        if (
            existing
            && (existing.label === 'Producător' || existing.label === 'Furnizor' || existing.label === '—')
            && label
            && label !== existing.label
        ) {
            existing.label = label;
        }
    };

    sources.forEach((source) => {
        if (source.hasDraftPricing) {
            const liveForm = source.liveForm || {};
            (source.livePricings || []).forEach((pricing) => {
                const key = resolveSupplierColumnKey(pricing, liveForm);
                if (!key) {
                    return;
                }
                registerColumn(key, resolveSupplierColumnLabel(pricing, liveForm));
            });
            const draftForm = source.form || {};
            (source.pricings || []).forEach((pricing) => {
                const baseKey = resolveSupplierColumnKey(pricing, draftForm);
                if (!baseKey || !source.draftColumnFlags?.[baseKey]?.fromDraftList) {
                    return;
                }
                registerColumn(
                    toDraftColumnKey(baseKey),
                    resolveSupplierColumnLabel(pricing, draftForm),
                );
            });
        } else {
            const mfrContext = source.form || {};
            (source.pricings || []).forEach((pricing) => {
                const key = resolveSupplierColumnKey(pricing, mfrContext);
                if (!key) {
                    return;
                }
                registerColumn(key, resolveSupplierColumnLabel(pricing, mfrContext));
            });
        }
        const productSim = simulationState?.[source.productCode] || {};
        const mfrContext = source.form || source.liveForm || {};
        Object.entries(productSim).forEach(([key, sim]) => {
            const pricing = simulationEntryToPricing(sim);
            const labelContext = {
                manufacturerName: sim.manufacturerName || mfrContext.manufacturerName,
                manufacturerCode: sim.manufacturerCode || mfrContext.manufacturerCode,
            };
            registerColumn(key, resolveSupplierColumnLabel(pricing, labelContext));
        });
    });

    const emptyCell = () => ({
        present: false,
        entryPriceRonExVat: null,
        finalEntryPriceExVat: null,
        supplierListPriceNew: null,
        currency: '',
        isLowest: false,
        deltaVsMin: null,
        deltaPctVsMin: null,
        tooltip: '',
        simulation: null,
        pricing: null,
        clientKey: null,
        fromDraftList: false,
        draftKind: null,
    });

    const buildPresentCell = ({
        form,
        pricing,
        sim,
        fromDraftList = false,
        draftKind = null,
    }) => {
        const computed = computeSimulatedEntryPricing(
            form,
            pricing,
            sim,
            pricingSettings,
        );
        return {
            present: true,
            entryPriceRonExVat: computed.entryPriceRonExVat,
            finalEntryPriceExVat: computed.finalEntryPriceExVat,
            supplierListPriceNew: parseNumericInput(
                sim.supplierListPriceNew ?? pricing.supplierListPriceNew,
            ),
            currency: String(sim.currency || pricing.currency || 'ron'),
            isLowest: false,
            deltaVsMin: null,
            deltaPctVsMin: null,
            tooltip: '',
            simulation: sim,
            pricing,
            clientKey: pricing.clientKey || sim.clientKey,
            fromDraftList: Boolean(fromDraftList),
            draftKind: draftKind || null,
        };
    };

    const rows = sources.map((source) => {
        const cells = {};
        const productSim = simulationState?.[source.productCode] || {};

        if (source.hasDraftPricing) {
            const liveForm = source.liveForm || source.form || {};
            const draftForm = source.form || {};
            const liveByKey = {};
            (source.livePricings || []).forEach((pricing) => {
                const key = resolveSupplierColumnKey(pricing, liveForm);
                if (key) {
                    liveByKey[key] = pricing;
                }
            });
            const draftByKey = {};
            (source.pricings || []).forEach((pricing) => {
                const key = resolveSupplierColumnKey(pricing, draftForm);
                if (key) {
                    draftByKey[key] = pricing;
                }
            });

            Object.entries(liveByKey).forEach(([key, pricing]) => {
                const sim = productSim[key] || extractEditableSimulationFields(pricing);
                cells[key] = buildPresentCell({
                    form: liveForm,
                    pricing,
                    sim,
                    fromDraftList: false,
                });
            });

            Object.entries(source.draftColumnFlags || {}).forEach(([baseKey, flag]) => {
                if (!flag?.fromDraftList) {
                    return;
                }
                const draftKey = toDraftColumnKey(baseKey);
                const pricing = draftByKey[baseKey];
                if (!pricing) {
                    return;
                }
                const sim = productSim[draftKey]
                    || extractEditableSimulationFields(pricing);
                cells[draftKey] = buildPresentCell({
                    form: draftForm,
                    pricing,
                    sim,
                    fromDraftList: true,
                    draftKind: flag.draftKind || null,
                });
            });
        } else {
            const mfrContext = source.form || {};
            (source.pricings || []).forEach((pricing) => {
                const key = resolveSupplierColumnKey(pricing, mfrContext);
                if (!key) {
                    return;
                }
                const sim = productSim[key] || extractEditableSimulationFields(pricing);
                cells[key] = buildPresentCell({
                    form: source.form,
                    pricing,
                    sim,
                    fromDraftList: false,
                });
            });
        }

        // Simulation-only furnizori (added in builder, not on the product/draft).
        Object.entries(productSim).forEach(([key, sim]) => {
            if (cells[key]?.present) {
                return;
            }
            const pricing = simulationEntryToPricing(sim);
            const formForSim = isDraftColumnKey(key) && source.form
                ? source.form
                : (source.liveForm || source.form);
            cells[key] = buildPresentCell({
                form: formForSim,
                pricing,
                sim,
                fromDraftList: isDraftColumnKey(key),
                draftKind: isDraftColumnKey(key)
                    ? (source.draftColumnFlags?.[resolveBaseSupplierColumnKey(key)]?.draftKind
                        || 'new_furnizor')
                    : null,
            });
        });

        columnMeta.forEach((_, key) => {
            if (!cells[key]) {
                cells[key] = emptyCell();
            }
        });

        const presentPeers = [];
        columnMeta.forEach((meta, key) => {
            const cell = cells[key];
            if (cell?.present && Number.isFinite(cell.entryPriceRonExVat)) {
                const peerLabel = cell.fromDraftList
                    ? `${meta.label} (listă în lucru)`
                    : meta.label;
                presentPeers.push({
                    key,
                    label: peerLabel,
                    price: cell.entryPriceRonExVat,
                });
            }
        });

        const presentPrices = presentPeers.map((peer) => peer.price);
        const minEntry = presentPrices.length ? Math.min(...presentPrices) : null;

        Object.entries(cells).forEach(([key, cell]) => {
            if (!cell.present || minEntry == null || !Number.isFinite(cell.entryPriceRonExVat)) {
                cell.tooltip = cell.present ? '' : 'Fără ofertă de la acest furnizor';
                return;
            }
            const delta = round2(cell.entryPriceRonExVat - minEntry);
            cell.deltaVsMin = delta;
            cell.deltaPctVsMin = minEntry > 0 ? round2((delta / minEntry) * 100) : null;
            // isLowest set globally after all rows (lowest final RON across all product codes).
            cell.isLowest = false;
            cell.tooltip = formatEntryPriceCompareTooltip(
                cell.entryPriceRonExVat,
                presentPeers.filter((peer) => peer.key !== key),
            );
        });

        return {
            productCode: source.productCode,
            productName: source.productName,
            cells,
            minEntryPriceRonExVat: minEntry,
        };
    });

    const globalPrices = [];
    rows.forEach((row) => {
        Object.values(row.cells || {}).forEach((cell) => {
            if (cell?.present && Number.isFinite(cell.entryPriceRonExVat)) {
                globalPrices.push(cell.entryPriceRonExVat);
            }
        });
    });
    const globalMinEntry = globalPrices.length ? Math.min(...globalPrices) : null;
    if (globalMinEntry != null) {
        rows.forEach((row) => {
            Object.values(row.cells || {}).forEach((cell) => {
                if (
                    cell?.present
                    && Number.isFinite(cell.entryPriceRonExVat)
                    && round2(cell.entryPriceRonExVat - globalMinEntry) === 0
                ) {
                    cell.isLowest = true;
                }
            });
        });
    }

    const refCode = String(referenceProductCode || sources[0]?.productCode || '').trim();
    const refRow = rows.find((row) => row.productCode === refCode) || rows[0] || null;

    const columns = Array.from(columnMeta.values()).sort((a, b) => {
        const baseA = resolveBaseSupplierColumnKey(a.key);
        const baseB = resolveBaseSupplierColumnKey(b.key);
        const priceFor = (baseKey) => {
            const official = refRow?.cells?.[baseKey];
            const draft = refRow?.cells?.[toDraftColumnKey(baseKey)];
            const candidates = [official, draft]
                .filter((cell) => cell?.present && Number.isFinite(cell.entryPriceRonExVat))
                .map((cell) => cell.entryPriceRonExVat);
            return candidates.length ? Math.min(...candidates) : Number.POSITIVE_INFINITY;
        };
        if (baseA === baseB) {
            return Number(isDraftColumnKey(a.key)) - Number(isDraftColumnKey(b.key));
        }
        const priceA = priceFor(baseA);
        const priceB = priceFor(baseB);
        if (priceA !== priceB) {
            return priceA - priceB;
        }
        const labelCmp = String(a.label).localeCompare(String(b.label), 'ro');
        if (labelCmp !== 0) {
            return labelCmp;
        }
        return Number(isDraftColumnKey(a.key)) - Number(isDraftColumnKey(b.key));
    });

    return { columns, rows, globalMinEntryPriceRonExVat: globalMinEntry };
}

/**
 * @param {number} delta
 * @param {number|null} deltaPct
 * @returns {string}
 */
export function formatDeltaTooltip(delta, deltaPct) {
    const abs = Math.abs(delta);
    const sign = delta > 0 ? '+' : delta < 0 ? '−' : '';
    const pctPart = deltaPct == null
        ? ''
        : ` (${sign}${formatRoNumber(Math.abs(deltaPct))}%)`;
    return `${sign}${formatRoNumber(abs)} RON vs cel mai mic${pctPart}`;
}

/**
 * Tooltip lines: % (and RON) difference vs each other supplier on the same product.
 * @param {number} price
 * @param {Array<{ label: string, price: number }>} peers
 * @returns {string}
 */
export function formatEntryPriceCompareTooltip(price, peers) {
    if (!Array.isArray(peers) || !peers.length) {
        return 'Singurul preț de achiziție pe acest produs';
    }
    return peers.map((peer) => {
        const delta = round2(Number(price) - Number(peer.price));
        const pct = Number(peer.price) > 0
            ? round2((delta / Number(peer.price)) * 100)
            : null;
        const sign = delta > 0 ? '+' : delta < 0 ? '−' : '';
        const pctText = pct == null
            ? '—'
            : `${sign}${formatRoNumber(Math.abs(pct))}%`;
        const ronText = `${sign}${formatRoNumber(Math.abs(delta))} RON`;
        return `vs ${peer.label}: ${pctText} (${ronText})`;
    }).join('\n');
}

/**
 * @param {number} value
 * @returns {number}
 */
function round2(value) {
    return Math.round((Number(value) + Number.EPSILON) * 100) / 100;
}

/**
 * @param {number} value
 * @returns {string}
 */
function formatRoNumber(value) {
    return Number(value).toLocaleString('ro-RO', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
}

/**
 * Patch one editable field in simulation state.
 * @param {Record<string, Record<string, Record<string, unknown>>>>} state
 * @param {string} productCode
 * @param {string} columnKey
 * @param {string} field
 * @param {unknown} value
 * @returns {Record<string, Record<string, Record<string, unknown>>>>}
 */
export function patchSimulationField(state, productCode, columnKey, field, value) {
    const next = cloneDeep(state || {});
    if (!next[productCode]) {
        next[productCode] = {};
    }
    if (!next[productCode][columnKey]) {
        next[productCode][columnKey] = {};
    }
    next[productCode][columnKey][field] = value;
    return next;
}

/**
 * Builds a builder form for a product with simulation overrides applied on each furnizor tab.
 * Uses one shared source form so clientKeys stay stable for orange highlight baselines.
 * When a lista-în-lucru draft is provided, that becomes the official/simulation baseline.
 * @param {Record<string, unknown>} item
 * @param {Record<string, Record<string, unknown>>|null|undefined} productSimulation
 * @param {Record<string, unknown>|null|undefined} [draft]
 * @param {{ preferDraft?: boolean }} [options]
 * @returns {{ officialForm: Record<string, unknown>, simulationForm: Record<string, unknown> }}
 */
export function buildSimulationBuilderForms(item, productSimulation, draft = null, options = {}) {
    const preferDraft = options.preferDraft !== false;
    const source = buildProductCompareSource(
        item,
        preferDraft ? draft : null,
    );
    const flatSim = flattenSimulationStateForBuilder(productSimulation, preferDraft);
    const officialForm = cloneDeep(source.form);
    const simulationForm = cloneDeep(source.form);
    if (!Array.isArray(simulationForm.furnizorPricings)) {
        simulationForm.furnizorPricings = [];
    }
    const simByColumn = flatSim || {};
    const matchedKeys = new Set();

    simulationForm.furnizorPricings.forEach((pricing) => {
        const columnKey = resolveSupplierColumnKey(pricing, simulationForm);
        const overrides = simByColumn[columnKey];
        if (!overrides) {
            return;
        }
        matchedKeys.add(columnKey);
        if (overrides.clientKey) {
            pricing.clientKey = overrides.clientKey;
        }
        if (overrides.furnizorId !== undefined) {
            pricing.furnizorId = overrides.furnizorId;
        }
        if (overrides.furnizorCode !== undefined) {
            pricing.furnizorCode = overrides.furnizorCode;
        }
        if (overrides.furnizorName !== undefined) {
            pricing.furnizorName = overrides.furnizorName;
        }
        if (overrides.usesManufacturerAsFurnizor !== undefined) {
            pricing.usesManufacturerAsFurnizor = Boolean(
                overrides.usesManufacturerAsFurnizor,
            );
        }
        if (overrides.isDefault !== undefined) {
            pricing.isDefault = Boolean(overrides.isDefault);
        }
        SUPPLIER_COMPARE_EDIT_FIELDS.forEach((field) => {
            if (overrides[field] === undefined) {
                return;
            }
            if (field === 'discounts') {
                pricing.discounts = Array.isArray(overrides.discounts)
                    ? [...overrides.discounts]
                    : [];
                return;
            }
            if (field === 'transportCostBreakdown') {
                pricing.transportCostBreakdown = cloneDeep(
                    overrides.transportCostBreakdown,
                );
                return;
            }
            if (field === 'stampDutyBreakdown') {
                pricing.stampDutyBreakdown = cloneDeep(
                    overrides.stampDutyBreakdown,
                );
                return;
            }
            pricing[field] = overrides[field];
        });
    });

    Object.entries(simByColumn).forEach(([columnKey, overrides]) => {
        if (matchedKeys.has(columnKey) || !overrides) {
            return;
        }
        simulationForm.furnizorPricings.push(simulationEntryToPricing(overrides));
    });

    const active = simulationForm.furnizorPricings?.find(
        (row) => row.clientKey === simulationForm.activeFurnizorPricingKey,
    ) || simulationForm.furnizorPricings?.[0];
    if (active) {
        simulationForm.activeFurnizorPricingKey = active.clientKey;
        Object.assign(simulationForm, extractEditableSimulationFields(active));
        simulationForm.discounts = Array.isArray(active.discounts)
            ? [...active.discounts]
            : [];
    }
    return { officialForm, simulationForm };
}

/**
 * @deprecated use buildSimulationBuilderForms
 * @param {Record<string, unknown>} item
 * @param {Record<string, Record<string, unknown>>|null|undefined} productSimulation
 * @returns {Record<string, unknown>}
 */
export function buildSimulationBuilderForm(item, productSimulation) {
    return buildSimulationBuilderForms(item, productSimulation).simulationForm;
}

/**
 * Writes builder furnizor tabs back into simulation_state for one product.
 * When draftColumnFlags are provided, changed/new furnizori are stored under
 * `draft:` twin keys so lista-actuală columns remain separate.
 * @param {Record<string, Record<string, Record<string, unknown>>>>} state
 * @param {string} productCode
 * @param {Record<string, unknown>} form
 * @param {Record<string, { fromDraftList?: boolean }>|null|undefined} [draftColumnFlags]
 * @returns {Record<string, Record<string, Record<string, unknown>>>>}
 */
export function applyBuilderFormToSimulationState(
    state,
    productCode,
    form,
    draftColumnFlags = null,
) {
    const next = cloneDeep(state || {});
    const existing = next[productCode] || {};
    const mapped = mapBuilderFormToSimulationColumns(form, draftColumnFlags);
    const hasDraftFlags = Boolean(
        draftColumnFlags && Object.keys(draftColumnFlags).length,
    );

    if (!hasDraftFlags) {
        const preservedDraftTwins = {};
        Object.entries(existing).forEach(([key, value]) => {
            if (isDraftColumnKey(key)) {
                preservedDraftTwins[key] = value;
            }
        });
        next[productCode] = {
            ...preservedDraftTwins,
            ...mapped,
        };
        return next;
    }

    const merged = {};
    Object.entries(existing).forEach(([key, value]) => {
        if (!isDraftColumnKey(key)) {
            merged[key] = value;
        }
    });
    Object.assign(merged, mapped);
    next[productCode] = merged;
    return next;
}

/**
 * True when simulated editable fields differ from the official baseline for a product.
 * @param {Record<string, Record<string, unknown>>|null|undefined} baselineProduct
 * @param {Record<string, Record<string, unknown>>|null|undefined} simulatedProduct
 * @returns {boolean}
 */
export function productHasSimulationChanges(baselineProduct, simulatedProduct) {
    if (!simulatedProduct || typeof simulatedProduct !== 'object') {
        return false;
    }
    const baseline = baselineProduct || {};
    const keys = new Set([
        ...Object.keys(baseline),
        ...Object.keys(simulatedProduct),
    ]);
    for (const columnKey of keys) {
        if (!baseline[columnKey] && simulatedProduct[columnKey]) {
            return true;
        }
        if (baseline[columnKey] && !simulatedProduct[columnKey]) {
            return true;
        }
        const baseFields = extractEditableSimulationFields(baseline[columnKey] || {});
        const simFields = extractEditableSimulationFields(simulatedProduct[columnKey] || {});
        for (const field of SUPPLIER_COMPARE_EDIT_FIELDS) {
            if (field === 'transportCostBreakdown') {
                const left = JSON.stringify(baseFields.transportCostBreakdown || null);
                const right = JSON.stringify(simFields.transportCostBreakdown || null);
                if (left !== right) {
                    return true;
                }
                continue;
            }
            if (field === 'stampDutyBreakdown') {
                const left = JSON.stringify(baseFields.stampDutyBreakdown || null);
                const right = JSON.stringify(simFields.stampDutyBreakdown || null);
                if (left !== right) {
                    return true;
                }
                continue;
            }
            if (field === 'discounts') {
                const left = JSON.stringify(baseFields.discounts || []);
                const right = JSON.stringify(simFields.discounts || []);
                if (left !== right) {
                    return true;
                }
                continue;
            }
            const left = String(baseFields[field] ?? '').trim();
            const right = String(simFields[field] ?? '').trim();
            if (left !== right) {
                return true;
            }
        }
    }
    return false;
}

/**
 * @param {unknown} value
 * @returns {number}
 */
export function parseCompareNumeric(value) {
    return parseNumericInput(value);
}

/**
 * Format a date as ro-RO calendar day for default simulation names.
 * @param {Date} [date]
 * @returns {string}
 */
export function formatSimulationNameDate(date = new Date()) {
    const d = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(d.getTime())) {
        return '';
    }
    return d.toLocaleDateString('ro-RO', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    });
}

/**
 * Default simulation name = date (n); progressive starts at 1.
 * @param {Iterable<string>|null|undefined} existingNames
 * @param {Date} [date]
 * @returns {string}
 */
export function buildDefaultSimulationName(existingNames, date = new Date()) {
    const day = formatSimulationNameDate(date);
    const base = day || 'Simulare';
    const names = new Set(
        [...(existingNames || [])]
            .map((name) => String(name || '').trim())
            .filter(Boolean),
    );
    let index = 1;
    while (names.has(`${base} (${index})`)) {
        index += 1;
    }
    return `${base} (${index})`;
}

const LAST_CODES_STORAGE_PREFIX = 'priceListSupplierCompare:lastProductCodes';

/**
 * Storage is per price-list scope AND per product (anchor).
 * Opening product A must not pull codes from sessions started on other products.
 * @param {string} priceListType
 * @param {number|string|null} priceListId
 * @param {string} productCode
 * @returns {string}
 */
export function lastProductCodesStorageKey(priceListType, priceListId, productCode) {
    const code = String(productCode || '').trim() || '_none';
    return `${LAST_CODES_STORAGE_PREFIX}:${priceListType || 'general'}:${priceListId || 'none'}:${code}`;
}

/**
 * @param {string} priceListType
 * @param {number|string|null} priceListId
 * @param {string} productCode - product whose last session set to load
 * @returns {string[]}
 */
export function loadLastSimulationProductCodes(priceListType, priceListId, productCode) {
    if (typeof localStorage === 'undefined') {
        return [];
    }
    const code = String(productCode || '').trim();
    if (!code) {
        return [];
    }
    try {
        const raw = localStorage.getItem(
            lastProductCodesStorageKey(priceListType, priceListId, code),
        );
        if (!raw) {
            return [];
        }
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) {
            return [];
        }
        return parsed.map((item) => String(item || '').trim()).filter(Boolean);
    } catch {
        return [];
    }
}

/**
 * Saves the working product-code set for one product's compare sessions.
 * @param {string} priceListType
 * @param {number|string|null} priceListId
 * @param {string} productCode - product that owns this last-session memory
 * @param {Iterable<string>} productCodes
 */
export function saveLastSimulationProductCodes(
    priceListType,
    priceListId,
    productCode,
    productCodes,
) {
    if (typeof localStorage === 'undefined') {
        return;
    }
    const owner = String(productCode || '').trim();
    if (!owner) {
        return;
    }
    const codes = [...(productCodes || [])]
        .map((item) => String(item || '').trim())
        .filter(Boolean);
    try {
        localStorage.setItem(
            lastProductCodesStorageKey(priceListType, priceListId, owner),
            JSON.stringify(codes),
        );
    } catch {
        // ignore quota / private mode
    }
}

/**
 * Merge anchor product with last-session codes (anchor first, unique, still in catalogue).
 * @param {string} anchorCode
 * @param {string[]} lastCodes
 * @param {Record<string, unknown>} productByCode
 * @returns {string[]}
 */
export function resolveNewSimulationProductCodes(anchorCode, lastCodes, productByCode) {
    const anchor = String(anchorCode || '').trim();
    const available = productByCode || {};
    const ordered = [];
    const seen = new Set();
    const push = (code) => {
        const normalized = String(code || '').trim();
        if (!normalized || seen.has(normalized) || !available[normalized]) {
            return;
        }
        seen.add(normalized);
        ordered.push(normalized);
    };
    push(anchor);
    (lastCodes || []).forEach(push);
    return ordered;
}
